import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText, Download, Filter, Search, X, Users, Wallet, TrendingUp,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight
} from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

/* ───────────── Configuration métier ───────────── */
const normaliserCode = (code) => (code ?? '').toString().trim().toUpperCase();

const PRIMES = [
  { key: 'prime_variable', label: 'Variable' },
  { key: 'prime_anciennete', label: 'Ancienneté' },
  { key: 'prime_caisse', label: 'Caisse' },
  { key: 'prime_responsabilite', label: 'Responsabilité' },
  { key: 'prime_astreinte', label: 'Astreinte' },
  { key: 'prime_de_deplacement', label: 'Déplacement' },
  { key: 'prime_autre', label: 'Autre' },
  { key: 'prime_gratification', label: 'Gratification' },
];

const CI_CHARGES = {
  patronales: [
    { label: 'CNPS Patronale', key: 'cnps_patronale' },
    { label: 'CMU', key: 'cmu_patronal' },
    { label: 'Accident de Travail', key: 'accident_travail' },
    { label: 'Prestations Familiales', key: 'prestation_familiale' },
    { label: "FDFP Taxe d'apprentissage", key: 'fdfp' },
    { label: 'FDFP Formation continue', key: 'fdfp_continu' },
  ],
  salariales: [
    { label: 'CNPS', key: 'cnps' },
    { label: 'CMU', key: 'cmu_employe' },
    { label: 'Impôt brut avant RITS', key: 'Impot_brut_avant_RICF' },
    { label: 'Charges de Famille (RITS)', key: 'Charges_Famille_RICF' },
    { label: 'Total RITS', key: 'Total_Impot' },
    { label: 'Caisse sociale', key: 'caisse_sociale' },
  ],
};

const CHARGES_PAR_FILIALE = {
  BJ: {
    patronales: [
      { label: 'CNSS Patronale', key: 'cnss_patronale' },
      { label: 'VPS', key: 'vps' },
    ],
    salariales: [
      { label: 'CNSS', key: 'cnss' },
      { label: 'IPTS brut', key: 'ipts_brut' },
      { label: 'Abattement', key: 'abattement' },
      { label: 'IPTS net', key: 'iptsnet' },
      { label: 'Redevance ORTB', key: 'redevance_ortb' },
      { label: 'Caisse sociale', key: 'caisse_sociale' },
    ],
  },
  CI: CI_CHARGES,
  CI_HOOPE: CI_CHARGES,
  CM: {
    patronales: [
      { label: 'Taxe Communale', key: 'taxe_communale' },
      { label: 'Redevance AV', key: 'redevence_AV' },
      { label: 'CNPS Pension Vieillesse', key: 'cnps_patronale' },
      { label: 'IRPP + CAC', key: 'irpp_cac', composite: true },
      { label: 'Crédit Foncier Patronal', key: 'credit_foncier_patron' },
      { label: 'FNE', key: 'FNE' },
      { label: 'Allocation Familiale', key: 'allocation_familiale' },
    ],
    salariales: [
      { label: 'CNPS Pension', key: 'cnps' },
      { label: 'Impôt Professionnel', key: 'impot_professionnel' },
      { label: 'Impôt Démocratique', key: 'impot_democratique' },
      { label: 'Crédit Foncier Salarial', key: 'credit_foncier' },
      { label: 'Caisse sociale', key: 'caisse_sociale' },
    ],
  },
};

const GROUPS = {
  remu: { label: 'Rémunération', head: 'bg-blue-50 text-blue-800' },
  primes: { label: 'Primes', head: 'bg-gray-100 text-gray-700', toggle: true },
  patronales: { label: 'Charges patronales', head: 'bg-blue-100/60 text-blue-900', toggle: true },
  salariales: { label: 'Retenues salariales', head: 'bg-gray-100 text-gray-700', toggle: true },
  net: { label: 'Net', head: 'bg-blue-50 text-blue-800' },
};

// Colonnes numériques (le tableau, les totaux et l'export sont tous générés à partir de cette liste)
const buildColumns = (code) => {
  const cfg = CHARGES_PAR_FILIALE[code];
  const num = (k) => (r) => toNumber(r[k]);
  const cols = [
    ...PRIMES.map((p) => ({ key: p.key, label: p.label, group: 'primes', get: num(p.key) })),
    { key: 'brut', label: 'Salaire brut', group: 'remu', get: num('salaire_brut'), money: true, tint: 'bg-green-50/70' },
    { key: 'fixe', label: 'Salaire fixe', group: 'remu', get: num('salaire_fixe'), money: true, tint: 'bg-orange-50/70' },
  ];
  if (cfg) {
    cfg.patronales.forEach((c) => cols.push({
      key: `pat_${c.key}`, label: c.label, group: 'patronales',
      get: c.composite ? (r) => toNumber(r.iptsnet) + toNumber(r.cac) : num(c.key),
    }));
    cfg.salariales.forEach((c) => cols.push({ key: `sal_${c.key}`, label: c.label, group: 'salariales', get: num(c.key) }));
  }
  cols.push({ key: 'net', label: 'Salaire net', group: 'net', get: num('salaire_net'), money: true, tint: 'bg-red-50/70', stickyRight: true });
  return cols;
};

/* ───────────── Utilitaires ───────────── */
const EMPTY_FILTERS = { type_personnel_id: '', actif: '', type_contrat_id: '', business_unit_id: '', departement_id: '', marque_id: '' };

// Les montants peuvent arriver en texte ("150 000,50")
const toNumber = (v) => {
  if (typeof v === 'number') return v;
  const n = parseFloat(String(v ?? '').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const nf = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
const money = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const normalize = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const initials = (r) => `${(r.prenom || '?')[0]}${(r.nom || '')[0] || ''}`.toUpperCase();

/* ───────────── Petits composants ───────────── */
const Stat = ({ icon: Icon, label, value, hint }) => (
  <div className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4">
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="truncate text-xl font-semibold text-gray-900">{value}</p>
      {hint && <p className="truncate text-xs text-gray-400">{hint}</p>}
    </div>
  </div>
);

const Select = ({ label, value, onChange, children }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-gray-600">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
    >
      {children}
    </select>
  </label>
);

/* ───────────── Page ───────────── */
const ReportingPaie = () => {
  const { filialeId, isConsolidated } = useFiliale();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'normal';
  const id = searchParams.get('id');
  const date = searchParams.get('date');

  const [loading, setLoading] = useState(true);
  const [periodeInfo, setPeriodeInfo] = useState(null);
  const [data, setData] = useState([]);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'employe', dir: 'asc' });
  const [hidden, setHidden] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [options, setOptions] = useState({ typesPersonnel: [], typesContrat: [], businessUnits: [], departements: [], marques: [] });

  // Code filiale lu dans les données (détermine les colonnes de charges)
  const filialeCodes = useMemo(() => {
    const codes = [...new Set(data.map(r => normaliserCode(r.filiale_code)))].filter(Boolean);
    return codes;
  }, [data]);
  const columns = useMemo(() => {
    const allCols = [];
    filialeCodes.forEach(code => {
      const cols = buildColumns(code);
      // Ajouter uniquement les colonnes spécifiques à la filiale (patronales et salariales)
      // car les colonnes communes (primes, brut, fixe, net) sont déjà ajoutées une fois
      const specificCols = cols.filter(c => c.group === 'patronales' || c.group === 'salariales');
      specificCols.forEach(c => {
        const originalKey = c.key;
        c.label = `${code} - ${c.label}`; // Préfixer avec le code filiale pour distinguer
        c.key = `${code}_${originalKey}`; // Préfixer la clé React pour éviter les doublons
        c.get = (r) => {
          // Extraire la clé originale (enlever le préfixe du code filiale)
          const keyFromDb = originalKey.replace(/^pat_/, '').replace(/^sal_/, '');
          if (c.group === 'patronales' && keyFromDb === 'irpp_cac') {
            return toNumber(r.iptsnet) + toNumber(r.cac);
          }
          return toNumber(r[keyFromDb]);
        };
      });
      allCols.push(...specificCols);
    });
    // Colonnes communes (primes, brut, fixe) ajoutées une seule fois, sans le net
    const commonCols = buildColumns('CI').filter(c => c.group !== 'patronales' && c.group !== 'salariales' && c.group !== 'net');
    // Ajouter le net en dernier
    const netCol = buildColumns('CI').find(c => c.group === 'net');
    return [...commonCols, ...allCols, ...(netCol ? [netCol] : [])];
  }, [filialeCodes]);
  const visible = useMemo(() => columns.filter((c) => !hidden.has(c.group)), [columns, hidden]);
  const toggleableGroups = useMemo(
    () => Object.keys(GROUPS).filter((g) => GROUPS[g].toggle && columns.some((c) => c.group === g)),
    [columns]
  );

  /* — Période — */
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await http.get(`/api/paies?isInterim=${type === 'interim'}`);
        const paies = Array.isArray(res.data) ? res.data : [];
        setPeriodeInfo(paies.find((p) => Number(type === 'interim' ? p.idpaie_interime : p.idpaie) === Number(id)) || null);
      } catch (e) {
        console.error('Erreur récupération période:', e);
        setPeriodeInfo(null);
      }
    })();
  }, [type, id]);

  /* — Listes des filtres — */
  useEffect(() => {
    const params = !isConsolidated && filialeId ? { filiale_id: filialeId } : {};
    const get = (url, p) => http.get(url, p ? { params: p } : undefined)
      .then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []);
    (async () => {
      const [typesPersonnel, typesContrat, businessUnits, departements, marques] = await Promise.all([
        get('/api/types-personnel', params), get('/api/types-contrat'), get('/api/business-units'),
        get('/api/departements'), get('/api/marques'),
      ]);
      setOptions({ typesPersonnel, typesContrat, businessUnits, departements, marques });
    })();
  }, [filialeId, isConsolidated]);

  /* — Données (seuls les filtres renseignés sont envoyés) — */
  useEffect(() => {
    if (!id && !date) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
        if (!isConsolidated && filialeId && filialeId !== 'all') params.filiale_id = filialeId;
        if (date) params.date = date;
        if (id) params.paie_id = id;
        console.log('Reporting - isConsolidated:', isConsolidated, 'filialeId:', filialeId, 'params:', params);
        const endpoint = type === 'interim' ? '/api/paie/reporting-interim' : '/api/paie/reporting';
        const res = await http.get(endpoint, { params });
        if (!cancelled) {
          const responseData = Array.isArray(res.data) ? res.data : [];
          const filialesInData = [...new Set(responseData.map(r => r.filiale_code))].filter(Boolean);
          console.log('Filiales dans les données:', filialesInData);
          setData(responseData);
        }
      } catch (e) {
        console.error('Erreur récupération données:', e);
        if (!cancelled) setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filters, filialeId, isConsolidated, type, id, date]);

  useEffect(() => { setPage(1); }, [query, filters, sort, pageSize]);

  /* — Recherche + tri — */
  const rows = useMemo(() => {
    const q = normalize(query).trim();
    const list = q
      ? data.filter((r) => normalize(`${r.nom} ${r.prenom} ${r.prenom} ${r.nom} ${r.matricule}`).includes(q))
      : data;
    const col = columns.find((c) => c.key === sort.key);
    const getter = col ? col.get : (r) => normalize(`${r.nom} ${r.prenom}`);
    return [...list].sort((a, b) => {
      const x = getter(a), y = getter(b);
      const cmp = x < y ? -1 : x > y ? 1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [data, query, sort, columns]);

  // Totaux sur l'ensemble des résultats (pas seulement la page affichée)
  const totals = useMemo(() => {
    const t = {};
    columns.forEach((c) => { t[c.key] = rows.reduce((s, r) => s + c.get(r), 0); });
    return t;
  }, [rows, columns]);
  const totalPatronales = useMemo(
    () => columns.filter((c) => c.group === 'patronales').reduce((s, c) => s + (totals[c.key] || 0), 0),
    [columns, totals]
  );

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pageCount);
  const pageRows = rows.slice((current - 1) * pageSize, current * pageSize);

  /* — En-têtes de groupes (calculés sur les colonnes visibles) — */
  const groupSpans = useMemo(() => {
    const out = [];
    visible.forEach((c) => {
      const last = out[out.length - 1];
      if (last && last.group === c.group) last.span += 1;
      else out.push({ group: c.group, span: 1 });
    });
    return out;
  }, [visible]);

  /* — Pastilles de filtres actifs — */
  const labelOf = (key, value) => {
    const pick = (list, idKey, lbl) => list.find((x) => String(x[idKey]) === String(value))?.[lbl] ?? value;
    switch (key) {
      case 'type_personnel_id': return pick(options.typesPersonnel, 'idtype_personnel', 'libelle');
      case 'type_contrat_id': return pick(options.typesContrat, 'idtype_contrat', 'nom');
      case 'business_unit_id': return pick(options.businessUnits, 'idbusiness_unit', 'libelle');
      case 'departement_id': return pick(options.departements, 'iddepartement', 'libelle_departement');
      case 'marque_id': return pick(options.marques, 'id', 'libelle');
      case 'actif': return value === '1' ? 'Employés actifs' : 'Employés inactifs';
      default: return value;
    }
  };
  const activeFilters = Object.entries(filters).filter(([, v]) => v !== '');
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'employe' ? 'asc' : 'desc' }));
  const toggleGroup = (g) => setHidden((h) => { const n = new Set(h); n.has(g) ? n.delete(g) : n.add(g); return n; });

  /* — Export CSV : toutes les colonnes, résultats filtrés + recherche — */
  const handleExport = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const headers = ['Matricule', 'Nom', 'Prénom', ...columns.map((c) => c.label)];
    const lines = rows.map((r) => [r.matricule, r.nom, r.prenom, ...columns.map((c) => c.get(r))]);
    const csv = '\uFEFF' + [headers, ...lines].map((l) => l.map(esc).join(';')).join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    link.download = `reporting-paie-${type}-${id}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (!id && !date) {
    return (
      <div className="p-6">
        <BlueHeader title="Reporting de Paie" subtitle="Consultation et extraction des données de paie" />
        <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center">
          <FileText className="mx-auto mb-3 h-8 w-8 text-gray-400" />
          <p className="font-medium text-gray-700">Aucune période sélectionnée</p>
          <p className="mt-1 text-sm text-gray-500">Choisissez une période de paie pour afficher son reporting.</p>
        </div>
      </div>
    );
  }

  const cloturee = Number(periodeInfo?.etat) >= 2;
  const totalBrut = totals.brut || 0;
  const totalNet = totals.net || 0;
  const SortIcon = ({ k }) => {
    const on = sort.key === k;
    const Icon = on && sort.dir === 'asc' ? ChevronUp : ChevronDown;
    return <Icon className={`h-3.5 w-3.5 ${on ? 'text-blue-600' : 'text-gray-300'}`} />;
  };

  return (
    <div className="space-y-5 p-6">
      <BlueHeader title="Reporting de Paie" subtitle="Consultation et extraction des données de paie" />

      {/* Bandeau période */}
      {periodeInfo && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-blue-600">
            <FileText className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-blue-900">{periodeInfo.libelle_paie}</p>
            <p className="text-xs text-blue-700">Du {fmtDate(periodeInfo.date_debut)} au {fmtDate(periodeInfo.date_fin)}</p>
          </div>
          <span className={`ml-auto inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
            cloturee ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200'
          }`}>
            {cloturee ? 'Clôturée' : 'Non clôturée'}
          </span>
        </div>
      )}

      {/* Indicateurs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Users} label="Employés" value={rows.length.toLocaleString('fr-FR')}
          hint={query || activeFilters.length ? 'Selon la recherche et les filtres' : 'Sur la période'} />
        <Stat icon={Wallet} label="Total brut" value={money.format(totalBrut)} />
        <Stat icon={TrendingUp} label="Total net" value={money.format(totalNet)} />
        <Stat icon={FileText} label="Charges patronales" value={totalPatronales ? money.format(totalPatronales) : '—'}
          hint={filialeCodes.length > 0 ? `Filiales: ${filialeCodes.join(', ')}` : undefined} />
      </div>

      {/* Tableau */}
      <div className="rounded-xl border border-gray-200 bg-white">
        {/* Barre d'outils */}
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 p-4">
          <button
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              showFilters || activeFilters.length ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filtres
            {activeFilters.length > 0 && <span className="rounded-full bg-blue-600 px-1.5 text-xs text-white">{activeFilters.length}</span>}
          </button>

          <p className="text-sm text-gray-500">
            <span className="font-semibold text-gray-800">{rows.length.toLocaleString('fr-FR')}</span> résultat{rows.length > 1 ? 's' : ''}
          </p>

          <div className="ml-auto flex w-full flex-wrap items-center gap-3 sm:w-auto">
            {/* Recherche par employé */}
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un employé (nom, prénom, matricule)"
                aria-label="Rechercher un employé"
                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-9 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 [&::-webkit-search-cancel-button]:hidden"
              />
              {query && (
                <button onClick={() => setQuery('')} aria-label="Effacer la recherche"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <button
              onClick={handleExport}
              disabled={rows.length === 0}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Exporter
            </button>
          </div>
        </div>

        {/* Panneau de filtres */}
        {showFilters && (
          <div className="border-b border-gray-200 bg-gray-50/60 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Select label="Type de personnel" value={filters.type_personnel_id} onChange={(v) => setFilter('type_personnel_id', v)}>
                <option value="">Tous</option>
                {options.typesPersonnel.map((o) => <option key={o.idtype_personnel} value={o.idtype_personnel}>{o.libelle}</option>)}
              </Select>
              <Select label="Employé actif" value={filters.actif} onChange={(v) => setFilter('actif', v)}>
                <option value="">Tous</option>
                <option value="1">Oui</option>
                <option value="0">Non</option>
              </Select>
              <Select label="Type de contrat" value={filters.type_contrat_id} onChange={(v) => setFilter('type_contrat_id', v)}>
                <option value="">Tous</option>
                {options.typesContrat.map((o) => <option key={o.idtype_contrat} value={o.idtype_contrat}>{o.nom}</option>)}
              </Select>
              <Select label="Business Unit" value={filters.business_unit_id} onChange={(v) => setFilter('business_unit_id', v)}>
                <option value="">Toutes</option>
                {options.businessUnits.map((o) => <option key={o.idbusiness_unit} value={o.idbusiness_unit}>{o.libelle}</option>)}
              </Select>
              <Select label="Département" value={filters.departement_id} onChange={(v) => setFilter('departement_id', v)}>
                <option value="">Tous</option>
                {options.departements.map((o) => <option key={o.iddepartement} value={o.iddepartement}>{o.libelle_departement}</option>)}
              </Select>
              <Select label="Marque" value={filters.marque_id} onChange={(v) => setFilter('marque_id', v)}>
                <option value="">Toutes</option>
                {options.marques.map((o) => <option key={o.id} value={o.id}>{o.libelle}</option>)}
              </Select>
            </div>
          </div>
        )}

        {/* Pastilles de filtres + affichage des colonnes */}
        {(activeFilters.length > 0 || toggleableGroups.length > 0) && (
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3">
            {activeFilters.map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 rounded-full bg-blue-50 py-1 pl-3 pr-1.5 text-xs font-medium text-blue-700">
                {labelOf(k, v)}
                <button onClick={() => setFilter(k, '')} aria-label="Retirer ce filtre" className="rounded-full p-0.5 hover:bg-blue-100">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {activeFilters.length > 0 && (
              <button onClick={() => setFilters(EMPTY_FILTERS)} className="text-xs font-medium text-gray-500 hover:text-gray-800">
                Tout réinitialiser
              </button>
            )}

            {toggleableGroups.length > 0 && (
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">Colonnes affichées :</span>
                {toggleableGroups.map((g) => {
                  const on = !hidden.has(g);
                  return (
                    <button
                      key={g}
                      onClick={() => toggleGroup(g)}
                      aria-pressed={on}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                        on ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-400 line-through hover:text-gray-600'
                      }`}
                    >
                      {GROUPS[g].label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Contenu */}
        {loading ? (
          <div className="divide-y divide-gray-100">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex animate-pulse items-center gap-4 px-4 py-4">
                <div className="h-9 w-9 rounded-full bg-gray-100" />
                <div className="h-3 w-48 rounded bg-gray-100" />
                <div className="ml-auto h-3 w-24 rounded bg-gray-100" />
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Search className="mx-auto mb-3 h-9 w-9 text-gray-300" />
            <p className="font-medium text-gray-700">Aucun résultat</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">
              {query || activeFilters.length
                ? 'Modifiez votre recherche ou retirez des filtres pour élargir les résultats.'
                : "Aucun bulletin n'est disponible pour cette période."}
            </p>
            {(query || activeFilters.length > 0) && (
              <button
                onClick={() => { setQuery(''); setFilters(EMPTY_FILTERS); }}
                className="mt-4 rounded-lg bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
              >
                Effacer la recherche et les filtres
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="max-h-[70vh] overflow-auto">
              <table className="w-full border-separate border-spacing-0 text-sm">
                <thead className="sticky top-0 z-20">
                  {/* Groupes */}
                  <tr>
                    <th className="sticky left-0 z-30 border-b border-gray-200 bg-white" />
                    {groupSpans.map((g, i) => (
                      <th key={`${g.group}-${i}`} colSpan={g.span}
                        className={`border-b border-l border-gray-200 px-4 py-1.5 text-center text-xs font-semibold ${GROUPS[g.group].head}`}>
                        {GROUPS[g.group].label}
                      </th>
                    ))}
                  </tr>
                  {/* Colonnes */}
                  <tr className="bg-gray-50">
                    <th className="sticky left-0 z-30 min-w-[15rem] border-b border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-700">
                      <button onClick={() => toggleSort('employe')} className="inline-flex items-center gap-1 hover:text-gray-900">
                        Employé <SortIcon k="employe" />
                      </button>
                    </th>
                    {visible.map((c) => (
                      <th key={c.key}
                        className={`min-w-[7.5rem] max-w-[10rem] border-b border-gray-200 bg-gray-50 px-4 py-3 text-right text-xs font-semibold leading-tight text-gray-700 ${
                          c.stickyRight ? 'sticky right-0 z-10 border-l' : ''
                        }`}>
                        <button onClick={() => toggleSort(c.key)} className="inline-flex items-center justify-end gap-1 text-right hover:text-gray-900">
                          {c.label} <SortIcon k={c.key} />
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {pageRows.map((r, i) => (
                    <tr key={`${r.personnel_id ?? r.matricule}-${i}`} className="group">
                      <td className="sticky left-0 z-10 border-b border-gray-100 bg-white px-4 py-3 group-hover:bg-blue-50">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">
                            {initials(r)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-gray-900">{r.nom || '—'} {r.prenom}</p>
                            <p className="text-xs text-gray-500">{r.matricule || 'Sans matricule'}</p>
                          </div>
                        </div>
                      </td>
                      {visible.map((c) => {
                        const v = c.get(r);
                        return (
                          <td key={c.key}
                            className={`whitespace-nowrap border-b border-gray-100 px-4 py-3 text-right tabular-nums group-hover:bg-blue-50 ${
                              c.tint || 'bg-white'
                            } ${c.money ? 'font-semibold text-gray-900' : 'text-gray-700'} ${c.stickyRight ? 'sticky right-0 z-10 border-l border-l-gray-200' : ''}`}>
                            {c.money ? (v ? money.format(v) : '—') : (v ? nf.format(v) : <span className="text-gray-300">—</span>)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>

                <tfoot className="sticky bottom-0 z-20">
                  <tr>
                    <td className="sticky left-0 z-30 border-t-2 border-blue-200 bg-blue-50 px-4 py-3 font-semibold text-blue-900">
                      Total · {rows.length.toLocaleString('fr-FR')} employé{rows.length > 1 ? 's' : ''}
                    </td>
                    {visible.map((c) => (
                      <td key={c.key}
                        className={`whitespace-nowrap border-t-2 border-blue-200 bg-blue-50 px-4 py-3 text-right font-semibold tabular-nums text-blue-900 ${
                          c.stickyRight ? 'sticky right-0 z-30 border-l border-l-blue-200' : ''
                        }`}>
                        {c.money ? money.format(totals[c.key] || 0) : nf.format(totals[c.key] || 0)}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span>Lignes par page</span>
                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                  className="rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30">
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span>{(current - 1) * pageSize + 1}–{Math.min(current * pageSize, rows.length)} sur {rows.length.toLocaleString('fr-FR')}</span>
                <button onClick={() => setPage(current - 1)} disabled={current === 1} aria-label="Page précédente"
                  className="rounded-lg border border-gray-300 p-1.5 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button onClick={() => setPage(current + 1)} disabled={current === pageCount} aria-label="Page suivante"
                  className="rounded-lg border border-gray-300 p-1.5 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportingPaie;